import moment from "moment";
import { toast } from "react-toastify";
import Abilities from "services/permissions/abilities";
import FlowUtils from "services/flow/utils";
import FlowApi from "services/flow/api";
import MapApi from "services/map/api";
import DisplaysApi from "services/displays/api";
import AreaUtils from "services/areas/utils";
import ParkStatisticsUtils from "services/parkStatistics/utils";
import PedestrianStatisticsUtils from "services/pedestrianStatistics/utils";
import ParkStatisticsApi from "services/parkStatistics/api";
import PedestrianStatisticsApi from "services/pedestrianStatistics/api";

import LiveActionTypes from "./types";
import LiveUtils from "./utils";
import { DETAILS_API_TYPES } from "../common/enums";
import { AlertEventsApi } from "../alertFactory/alertEvents/api";
import IntlUtils from "services/intl/utils";

class LiveActioner {
  static setFlowDetailsStats(stats) {
    return {
      type: LiveActionTypes.SET_FLOW_DETAILS_STATS,
      stats,
    };
  }

  static setFlowGeneralStats(stats) {
    return {
      type: LiveActionTypes.SET_FLOW_GENERAL_STATS,
      stats,
    };
  }

  static setParkDetailsStats(stats) {
    return {
      type: LiveActionTypes.SET_PARK_DETAILS_STATS,
      stats,
    };
  }

  static setPedestrianDetailsStats(stats) {
    return {
      type: LiveActionTypes.SET_PEDESTRIAN_DETAILS_STATS,
      stats,
    };
  }

  static setLoading(statName) {
    return {
      type: LiveActionTypes.SET_LOADING,
      statName,
    };
  }

  static setFilters(data) {
    return {
      type: LiveActionTypes.SET_FILTERS,
      data,
    };
  }

  static setFilter(key, value) {
    return {
      type: LiveActionTypes.SET_FILTER,
      key,
      value,
    };
  }

  static setAutoRefresh(autoRefresh) {
    return {
      type: LiveActionTypes.SET_AUTO_REFRESH,
      autoRefresh,
    };
  }

  static setInitialized(value) {
    return {
      type: LiveActionTypes.SET_INITIALIZED,
      value,
    };
  }

  static setMap(map) {
    return {
      type: LiveActionTypes.SET_MAP_INFOS,
      map,
    };
  }

  static setSelectedLayers(layers) {
    return {
      type: LiveActionTypes.SET_SELECTED_LAYERS,
      layers,
    };
  }

  static setAlertEvents(data) {
    return {
      type: LiveActionTypes.SET_ALERT_EVENTS,
      data,
    };
  }

  static setAlertModalPosition(position) {
    return {
      type: LiveActionTypes.SET_ALERT_MODAL_POSITION,
      position,
    };
  }

  // Check if some required filters are missing
  static missingRequiredFilters(type = "default", filters) {
    if (
      type === "areas" &&
      (!filters?.areas || !filters?.timestamp || filters?.timestamp < 0)
    ) {
      return true;
    }

    if (
      type === "radius" &&
      (!filters?.radius || !filters?.position?.lat || !filters?.position?.lng)
    ) {
      return true;
    }

    return false;
  }

  static retrieveLayersInfos() {
    return (dispatch, getState) => {
      const contractId = getState().Contract.contract.id;
      const LayersInfosPromises = [
        MapApi.getAreasInfos({
          contract_id: contractId,
          types: [
            "PARK",
            "FLOW",
            "PARKANDFLOW",
            "UNEQUIPPED",
            "PEDESTRIANANDFLOW",
            "QUEUEMANAGEMENT",
          ],
        }),
      ];

      if (Abilities.ability.can("read", "tabs.flow")) {
        LayersInfosPromises.push(
          ...[
            MapApi.getPointsInfos({
              contract_id: contractId,
              types: ["FLOW", "UNEQUIPPED", "VIRTUAL"],
            }),
            MapApi.getPathsInfos({
              contract_id: contractId,
              types: ["FLOW", "UNEQUIPPED", "VIRTUAL", "TRAFFIC"],
            }),
          ]
        );
      }

      return Promise.all(LayersInfosPromises)
        .then(([areasInfos, pointsInfos, pathsInfos, displaysInfos]) => {
          AreaUtils.sortAreasByConfigMapAreasOrder(
            areasInfos.areas,
            getState()?.Map?.config?.flow?.areas
          );

          AreaUtils.sortAreasByConfigMapAreasOrder(
            areasInfos.areas,
            getState()?.Map?.config?.parking?.areas
          );

          const mapConfig = {
            areas: [
              ...(getState()?.Map?.config?.flow?.areas || []),
              ...(getState()?.Map?.config?.parking?.areas || []),
              ...(getState()?.Map?.config?.pedestrian?.queue_areas || []),
            ],
            points: [
              ...(getState()?.Map?.config?.flow?.points || []),
              ...(getState()?.Map?.config?.parking?.points || []),
            ],
            paths: [
              ...(getState()?.Map?.config?.flow?.paths || []),
              ...(getState()?.Map?.config?.parking?.paths || []),
            ],
          };

          // List of Points / Areas / Paths from API
          const formattedMapInfos = LiveUtils.formatLayersInfosFromApi(
            { ...areasInfos, ...pointsInfos, ...pathsInfos, ...displaysInfos },
            mapConfig
          );
          const currentSelectedLayers = LiveUtils.getSelectedLayers(
            getState()?.Live
          ) || [
            ...formattedMapInfos.areas,
            ...formattedMapInfos.paths,
            ...formattedMapInfos.points,
            ...formattedMapInfos.displays,
          ];
          dispatch(LiveActioner.setMap(formattedMapInfos));
          dispatch(LiveActioner.setSelectedLayers(currentSelectedLayers));
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    };
  }

  static retrieveDisplays() {
    return (dispatch, getState) => {
      const contractId = getState().Contract.contract.id;
      const mapData = getState().Live?.map;

      DisplaysApi.retrieveList({
        contract_id: contractId,
      }).then((result) => {
        const formattedDisplays = LiveUtils.formatDisplaysFromApi(
          result?.displays || [],
          getState()?.Map?.config?.displays?.displays || []
        );

        dispatch(
          LiveActioner.setMap({ ...mapData, displays: formattedDisplays })
        );
      });
    };
  }

  static retrieveAllPedestrianDetailsStats() {
    return (dispatch, getState) => {
      if (!Abilities.ability.can("read", "tabs.pedestrian_stats")) {
        return null;
      }

      const LiveState = getState()?.Live;
      const currentFloor = getState().Map?.config?.currentFloor;

      const allowedDetailsApiTypes = [
        DETAILS_API_TYPES.PEDESTRIAN_AND_FLOW,
        DETAILS_API_TYPES.QUEUE_MANAGEMENT,
      ];

      const currentSelectedLayers = LiveUtils.getSelectedLayersFromCurrentFloor(
        LiveState,
        currentFloor
      );
      const filteredAreas = currentSelectedLayers?.filter(
        (layer) => allowedDetailsApiTypes.indexOf(layer.apiType) >= 0
      );
      if (!filteredAreas?.length) return {};

      const filters = LiveUtils.getPedestrianDetailsFilters({
        timestamp: LiveState?.filters?.timestamp,
        areas: filteredAreas,
      });
      const queueAreas = filteredAreas
        .map((area) => {
          if (area.apiType === DETAILS_API_TYPES.QUEUE_MANAGEMENT) {
            return area.area_id;
          }
          return null;
        })
        .filter((area) => area !== null);

      const promises = [];

      if (filteredAreas?.length > 0) {
        promises.push(
          PedestrianStatisticsApi.getDetailsStats("areas", filters)
        );
      }

      if (queueAreas?.length > 0) {
        promises.push(
          PedestrianStatisticsApi.getQueueDetailsStats({
            timestamp: LiveState?.filters?.timestamp,
            areas: queueAreas,
          })
        );
      }

      if (promises?.length === 0) {
        dispatch(LiveActioner.setPedestrianDetailsStats({ data: {} }));
        return;
      }

      return Promise.all(promises).then((stats) => {
        const data = stats.map((stat) =>
          PedestrianStatisticsUtils.formatDetailsStatsFromApi(stat, queueAreas)
        );
        dispatch(LiveActioner.setPedestrianDetailsStats({ data }));
      });
    };
  }

  static retrieveAllParkDetailsStats() {
    return (dispatch, getState) => {
      if (!Abilities.ability.can("read", "tabs.park_stats")) {
        return null;
      }

      const LiveState = getState()?.Live;
      const currentFloor = getState().Map?.config?.currentFloor;

      const allowedDetailsApiTypes = [
        DETAILS_API_TYPES.PARK,
        DETAILS_API_TYPES.PARK_AND_FLOW,
      ];

      const currentSelectedLayers = LiveUtils.getSelectedLayersFromCurrentFloor(
        LiveState,
        currentFloor
      );
      const filteredAreas = currentSelectedLayers?.filter(
        (layer) => allowedDetailsApiTypes.indexOf(layer.apiType) >= 0
      );

      if (!filteredAreas?.length) return {};

      const filters = LiveUtils.getParkDetailsFilters({
        timestamp: LiveState?.filters?.timestamp,
        areas: filteredAreas,
        areaType: LiveState?.filters?.areaType,
      });

      return ParkStatisticsApi.getDetailsStats("areas", filters).then(
        (stats) => {
          const data = ParkStatisticsUtils.formatDetailsStatsFromApi(stats);
          dispatch(LiveActioner.setParkDetailsStats({ data }));
        }
      );
    };
  }

  static retrieveAllFlowDetailsStats() {
    return (dispatch, getState) => {
      if (!Abilities.ability.can("read", "tabs.flow")) {
        return null;
      }

      const currentSelectedLayers = LiveUtils.getSelectedLayersFromCurrentFloor(
        getState()?.Live,
        getState().Map?.config?.currentFloor
      );
      let detailsPromises = [];
      const allowedTypes = ["area", "point"];
      const allowedDetailsApiTypes = [
        DETAILS_API_TYPES.FLOW,
        DETAILS_API_TYPES.PARK_AND_FLOW,
        DETAILS_API_TYPES.PEDESTRIAN_AND_FLOW,
      ];

      detailsPromises = currentSelectedLayers
        .filter(
          (layer) =>
            allowedTypes.indexOf(layer.type) >= 0 &&
            allowedDetailsApiTypes.indexOf(layer.apiType) >= 0
        )
        .map((layer) => {
          return LiveActioner.retrieveFlowDetailsStats(layer, getState());
        });

      return Promise.all(detailsPromises).then((detailsStats) => {
        return dispatch(
          LiveActioner.setFlowDetailsStats({
            data: detailsStats.filter((d) => d !== null),
          })
        );
      });
    };
  }

  static retrieveFlowDetailsStats(layer, state) {
    const type = layer.type;

    if (type === "area" && layer?.apiType !== DETAILS_API_TYPES.FLOW) {
      return null;
    }

    const LiveState = state?.Live;
    const filters = LiveUtils.getFlowDetailsFilters(type, state);

    return FlowApi.getDetailsStats(layer?.id, type, filters).then((stats) => {
      const formattedDetailsStats = FlowUtils.formatDetailsStatsFromApi(
        {
          type,
          apiType: layer?.apiType,
          [`${type}_id`]: layer?.id, // point_id ; path_id ; area_id
          reqFilters: filters,
          ...stats,
        },
        LiveState
      );

      return formattedDetailsStats;
    });
  }

  static retrieveAllFlowGeneralStats() {
    return (dispatch, getState) => {
      if (!Abilities.ability.can("read", "tabs.flow")) {
        return null;
      }

      const currentSelectedLayers = LiveUtils.getSelectedLayersFromCurrentFloor(
        getState()?.Live,
        getState().Map?.config?.currentFloor
      );
      let generalPromises = [];
      const allowedTypes = ["path"];
      const allowedDetailsApiTypes = [
        DETAILS_API_TYPES.FLOW,
        DETAILS_API_TYPES.PARK_AND_FLOW,
        DETAILS_API_TYPES.PEDESTRIAN_AND_FLOW,
        DETAILS_API_TYPES.TRAFFIC,
      ];

      generalPromises = currentSelectedLayers
        .filter(
          (layer) =>
            allowedTypes.indexOf(layer.type) >= 0 &&
            allowedDetailsApiTypes.indexOf(layer.apiType) >= 0
        )
        .map((layer) =>
          LiveActioner.retrieveFlowGeneralStats(layer, getState())
        );

      return Promise.all(generalPromises).then((generalStats) => {
        return dispatch(
          LiveActioner.setFlowGeneralStats({ data: generalStats })
        );
      });
    };
  }

  static retrieveFlowGeneralStats(layer, state) {
    const type = layer.type;
    const filters = LiveUtils.getFlowGeneralFilters(type, state);

    if (layer?.apiType === DETAILS_API_TYPES.TRAFFIC) {
      return FlowApi.getTrafficStats(layer?.id, { timestamp: filters.to }).then(
        (stats) => {
          const formattedGeneralStats = {
            type,
            apiType: layer?.apiType,
            [`${type}_id`]: layer?.id, // point_id ; path_id ; area_id
            reqFilters: { timestamp: filters.to },
            ...stats,
          };

          return formattedGeneralStats;
        }
      );
    }

    return FlowApi.getGeneralStats(layer?.id, type, filters).then((stats) => {
      const formattedGeneralStats = {
        type,
        apiType: layer?.apiType,
        [`${type}_id`]: layer?.id, // point_id ; path_id ; area_id
        reqFilters: filters,
        ...stats,
      };

      return formattedGeneralStats;
    });
  }

  static retrieveAlertEvents() {
    return (dispatch, getState) => {
      if (!Abilities.ability.can("read", "tabs.alerts")) {
        return null;
      }

      const LiveState = getState()?.Live;
      const filters = LiveState?.alertEvents?.filters;
      const { contract } = getState().Contract;
      const { user } = getState().User;
      const { id: userId } = user;
      const { id } = contract;
      const { timestamp } = filters;

      const pagination = getState().Live.alertEvents.pagination;

      const alertEventsFilters = {
        contract_id: id,
        viewer: userId,
        types: filters.output_types,
        from: moment(timestamp).subtract(2, "hours").valueOf(),
        to: timestamp,
      };

      return AlertEventsApi.getAlertEvents(pagination, alertEventsFilters)
        .then((res) => {
          dispatch(LiveActioner.setAlertEvents(res));
        })
        .catch((error) => {
          const intl = IntlUtils.getIntlInstance();
          toast(intl.messages["toast.errors.errorOccuredContactSupport"], {
            position: toast.POSITION.BOTTOM_RIGHT,
            type: toast.TYPE.ERROR,
          });
          return Promise.reject(error);
        });
    };
  }
}

export default LiveActioner;
